import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  const lastUpdated = "December 31, 2024";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-dark to-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl md:text-4xl font-bold">Terms of Service</h1>
          <p className="mt-2 text-white/80">Last updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 space-y-8">
          
          {/* Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Agreement to Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              By accessing or using ACP Conference Planner, a product of Resurgence Technology Group, LLC ("the Service"), you agree to be bound by these 
              Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          {/* Description of Service */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Description of Service</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              ACP Conference Planner is an independent conference-management platform that helps users discover, plan, and attend Apostolic and Pentecostal 
              conferences and religious events. We provide event information, speaker profiles, venue details, 
              and travel planning resources.
            </p>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">User Accounts</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              When you create an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Acceptable Use</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>Use the Service for any unlawful purpose</li>
              <li>Submit false or misleading event information</li>
              <li>Impersonate another person or entity</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated tools to scrape or collect data</li>
              <li>Post content that is offensive, harmful, or inappropriate</li>
            </ul>
          </section>

          {/* Event Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Event Information</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We strive to provide accurate event information, but we do not guarantee the accuracy, 
              completeness, or reliability of any event listings. Event details are provided by third-party 
              organizers and may change without notice. Always verify event details with the official 
              organizer before making travel arrangements.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Third-Party Links</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              Our Service contains links to third-party websites and services (e.g., hotel booking sites, 
              flight search engines, event registration pages). We are not responsible for the content, 
              privacy practices, or terms of these external sites.
            </p>
          </section>

          {/* Payments and Support */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Payments and Support</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              Voluntary contributions made through our Support page are non-refundable and are not 
              tax-deductible. ACP Conference Planner is operated by Resurgence Technology Group, LLC as a for-profit business. Contributions 
              help maintain and improve the Service but do not entitle you to any additional features or services.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Intellectual Property</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              The Service and its original content, features, and functionality are owned by Resurgence 
              Technology Group, LLC and are protected by copyright, trademark, and other intellectual property laws. 
              Event information and speaker profiles remain the property of their respective owners.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Disclaimer of Warranties</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
              EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR 
              ERROR-FREE.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Limitation of Liability</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              IN NO EVENT SHALL RESURGENCE TECHNOLOGY GROUP, LLC BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT 
              LIMITED TO TRAVEL ARRANGEMENTS, EVENT ATTENDANCE, OR RELIANCE ON EVENT INFORMATION.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Termination</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We may terminate or suspend your account and access to the Service at our sole discretion, 
              without notice, for conduct that we believe violates these Terms or is harmful to other users, 
              us, or third parties.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Changes to Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of significant 
              changes by posting a notice on our website. Your continued use of the Service after changes 
              constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Governing Law</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              <a href="mailto:legal@conferenceplanner2026.com" className="text-primary hover:underline">
                legal@conferenceplanner2026.com
              </a>
            </p>
          </section>

        </div>

        {/* Back Link */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-primary hover:text-primary-dark font-medium inline-flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
